import {
    RssiGradientConfig,
    LAYER_TYPES,
    DEFAULT_RSSI_RANGE,
    LayerOptions
} from '../../../../../shared/image-layers/layer.types';
import {BaseLayer} from '../../../../../shared/image-layers/layers/BaseLayer';

const BASE_BAR_HEIGHT = 6;
const BASE_LABEL_MARGIN_TOP = 4;
const BASE_LABEL_FONT_SIZE = 12;
const LABEL_COLOR = 'rgba(209, 213, 219, 1)'; // Light gray

// Container box styling
const BOX_BACKGROUND = 'rgba(55, 65, 81, 0.8)';
const BOX_BORDER_COLOR = 'rgba(75, 85, 99, 1)';
const BASE_BOX_BORDER_WIDTH = 1;
const BASE_BOX_BORDER_RADIUS = 10;
const BASE_BOX_PADDING_Y = 6; // top/bottom
const BASE_BOX_PADDING_X = 8; // left/right

/** Style overrides for merge/export mode */
export interface RssiScaleBarStyleOverrides {
    boxPaddingX?: number;
    boxPaddingY?: number;
    fontSize?: number;
    labelMarginTop?: number;
    barHeight?: number;
    borderRadius?: number;
}

export interface RssiScaleBarLayerOptions extends LayerOptions {
    id?: string;
    styleOverrides?: RssiScaleBarStyleOverrides;
    width?: number;
}

export class RssiScaleBarLayer extends BaseLayer {
    private readonly config: RssiGradientConfig;
    private styleOverrides?: RssiScaleBarStyleOverrides;
    private readonly customWidth?: number;

    constructor(config?: Partial<RssiGradientConfig>, options?: RssiScaleBarLayerOptions) {
        super(options?.id ?? 'rssi-scale-bar', LAYER_TYPES.RSSI_GRADIENT, {
            visible: options?.visible ?? true,
            opacity: options?.opacity ?? 1,
            zIndex: options?.zIndex ?? 1
        });

        this.config = {
            ...DEFAULT_RSSI_RANGE,
            ...config
        };

        this.styleOverrides = options?.styleOverrides;
        this.customWidth = options?.width;
    }

    static getRecommendedHeight(): number {
        const contentHeight = BASE_BAR_HEIGHT + BASE_LABEL_MARGIN_TOP + BASE_LABEL_FONT_SIZE;
        return contentHeight + BASE_BOX_PADDING_Y * 2 + BASE_BOX_BORDER_WIDTH * 2;
    }

    static getRecommendedWidth(contentWidth: number): number {
        return contentWidth + BASE_BOX_PADDING_X * 2 + BASE_BOX_BORDER_WIDTH * 2;
    }

    private renderAtPosition(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        x: number = 0,
        y: number = 0
    ): void {
        const paddingX = this.styleOverrides?.boxPaddingX ?? BASE_BOX_PADDING_X;
        const paddingY = this.styleOverrides?.boxPaddingY ?? BASE_BOX_PADDING_Y;
        const fontSize = this.styleOverrides?.fontSize ?? BASE_LABEL_FONT_SIZE;
        const labelMarginTop = this.styleOverrides?.labelMarginTop ?? BASE_LABEL_MARGIN_TOP;
        const barHeight = this.styleOverrides?.barHeight ?? BASE_BAR_HEIGHT;
        const borderWidth = BASE_BOX_BORDER_WIDTH;
        const borderRadius = this.styleOverrides?.borderRadius ?? BASE_BOX_BORDER_RADIUS;

        this.renderBox(ctx, width, height, borderRadius, borderWidth, x, y);

        const contentX = x + paddingX + borderWidth;
        const contentY = y + paddingY + borderWidth;
        const contentWidth = width - paddingX * 2 - borderWidth * 2;

        // Draw gradient bar at calculated position
        this.renderGradientBar(ctx, contentWidth, barHeight, contentX, contentY);

        // Draw labels at calculated position
        this.renderLabels(ctx, contentWidth, fontSize, labelMarginTop, barHeight, contentX, contentY);
    }

    private renderBox(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        borderRadius: number,
        borderWidth: number,
        x: number,
        y: number
    ): void {
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, borderRadius);
        ctx.fillStyle = BOX_BACKGROUND;
        ctx.fill();

        // Draw border
        ctx.strokeStyle = BOX_BORDER_COLOR;
        ctx.lineWidth = borderWidth;
        ctx.stroke();
    }

    private renderGradientBar(
        ctx: CanvasRenderingContext2D,
        width: number,
        barHeight: number,
        x: number,
        y: number
    ): void {
        // Create HORIZONTAL gradient (left = red/poor, right = green/good)
        const gradient = ctx.createLinearGradient(x, y, x + width, y);
        const GRADIENT_STOPS = [
            {offset: 0, color: '#ED0000'}, // Red (poor signal)
            {offset: 0.33, color: '#F8A400'}, // Orange
            {offset: 0.66, color: '#FFF500'}, // Yellow
            {offset: 1, color: '#19E314'} // Green (good signal)
        ];

        GRADIENT_STOPS.forEach((stop) => {
            gradient.addColorStop(stop.offset, stop.color);
        });

        // Draw rounded rectangle
        const radius = barHeight / 2;
        ctx.beginPath();
        ctx.roundRect(x, y, width, barHeight, radius);
        ctx.fillStyle = gradient;
        ctx.fill();
    }

    private renderLabels(
        ctx: CanvasRenderingContext2D,
        width: number,
        fontSize: number,
        labelMarginTop: number,
        barHeight: number,
        x: number,
        y: number
    ): void {
        const {minRSSI, midRSSI, maxRSSI} = this.config;
        const labelY = y + barHeight + labelMarginTop + fontSize;

        ctx.font = `400 ${fontSize}px "Open Sans", sans-serif`;
        ctx.fillStyle = LABEL_COLOR;

        // Left label (min RSSI - poor signal)
        ctx.textAlign = 'left';
        ctx.fillText(`${minRSSI} dBm`, x, labelY);

        // Center label (mid RSSI)
        ctx.textAlign = 'center';
        ctx.fillText(`${midRSSI} dBm`, x + width / 2, labelY);

        // Right label (max RSSI - good signal)
        ctx.textAlign = 'right';
        ctx.fillText(`${maxRSSI} dBm`, x + width, labelY);
    }

    render(
        ctx: CanvasRenderingContext2D,
        containerWidth: number,
        containerHeight: number,
        renderOptions?: {
            /** Scale bar width ratio relative to canvas (for merge mode) */
            widthRatio?: number;
            /** Base width used for calculating scale factor (for merge mode) */
            baseWidth?: number;
            /** Bottom padding in pixels */
            bottomPadding?: number;
            /** Horizontal center offset */
            centerOffset?: number;
            /** Base style values for scaling (for merge mode) */
            baseStyles?: {
                boxPaddingX?: number;
                boxPaddingY?: number;
                fontSize?: number;
                labelMarginTop?: number;
                barHeight?: number;
                borderRadius?: number;
            };
        }
    ): void {
        // Check if this is merge mode (has widthRatio option)
        if (renderOptions && renderOptions.widthRatio !== undefined) {
            // Merge mode: scale proportionally to canvas
            this.renderScaledForMerge(ctx, containerWidth, containerHeight, {
                widthRatio: renderOptions.widthRatio,
                baseWidth: renderOptions.baseWidth,
                bottomPadding: renderOptions.bottomPadding,
                centerOffset: renderOptions.centerOffset,
                baseStyles: renderOptions.baseStyles
            });
        } else {
            // Display mode: use fixed width
            const scaleBarWidth = this.customWidth ?? 350;
            const scaleBarHeight = RssiScaleBarLayer.getRecommendedHeight();
            const bottomPadding = renderOptions?.bottomPadding ?? 10;
            const centerOffset = renderOptions?.centerOffset ?? 0;

            // Calculate position (centered horizontally, positioned at bottom with padding)
            const x = (containerWidth - scaleBarWidth) / 2 + centerOffset;
            const y = containerHeight - scaleBarHeight - bottomPadding;

            // Render at calculated position
            this.renderAtPosition(ctx, scaleBarWidth, scaleBarHeight, x, y);
        }
    }

    private renderScaledForMerge(
        ctx: CanvasRenderingContext2D,
        canvasWidth: number,
        canvasHeight: number,
        options: {
            widthRatio: number;
            baseWidth?: number;
            bottomPadding?: number;
            centerOffset?: number;
            baseStyles?: {
                boxPaddingX?: number;
                boxPaddingY?: number;
                fontSize?: number;
                labelMarginTop?: number;
                barHeight?: number;
                borderRadius?: number;
            };
        }
    ): void {
        const {
            widthRatio,
            baseWidth = 350,
            bottomPadding = 20,
            centerOffset = 5,
            baseStyles = {
                boxPaddingX: 8,
                boxPaddingY: 6,
                fontSize: 14,
                labelMarginTop: 4,
                barHeight: 6,
                borderRadius: 10
            }
        } = options;

        const scaleBarWidth = Math.round(canvasWidth * widthRatio);
        const scaleFactor = scaleBarWidth / baseWidth;
        const scaleBarHeight = Math.round(RssiScaleBarLayer.getRecommendedHeight() * scaleFactor);

        const scaleBarX = Math.round((canvasWidth - scaleBarWidth) / 2) + centerOffset;
        const scaleBarY = Math.round(canvasHeight - scaleBarHeight - bottomPadding);

        // Create style overrides based on scale factor
        const styleOverrides: RssiScaleBarStyleOverrides = {
            boxPaddingX: Math.round(baseStyles.boxPaddingX! * scaleFactor),
            boxPaddingY: Math.round(baseStyles.boxPaddingY! * scaleFactor),
            fontSize: Math.round(baseStyles.fontSize! * scaleFactor),
            labelMarginTop: Math.round(baseStyles.labelMarginTop! * scaleFactor),
            barHeight: Math.round(baseStyles.barHeight! * scaleFactor),
            borderRadius: Math.round(baseStyles.borderRadius! * scaleFactor)
        };

        // Create a temporary scaled layer for rendering
        const scaledLayer = new RssiScaleBarLayer(this.config, {styleOverrides});

        // Render the scaled layer at calculated position
        scaledLayer.renderAtPosition(ctx, scaleBarWidth, scaleBarHeight, scaleBarX, scaleBarY);
    }

    /**
     * Clone the layer
     */
    clone(
        overrides?: Partial<{
            id: string;
            visible: boolean;
            opacity: number;
            zIndex: number;
            config: Partial<RssiGradientConfig>;
        }>
    ): RssiScaleBarLayer {
        return new RssiScaleBarLayer(
            {...this.config, ...overrides?.config},
            {
                id: overrides?.id ?? `${this.id}-clone`,
                visible: overrides?.visible ?? this.visible,
                opacity: overrides?.opacity ?? this.opacity,
                zIndex: overrides?.zIndex ?? this.zIndex
            }
        );
    }
}
